import { Box, useTheme, alpha } from "@mui/material";

interface ProgressBarProps {
  value: number;
  max?: number;
  ariaLabel?: string;
}

// 4px coral fill on a muted track, with a smooth width transition. Used in
// the mini-map progress composite and (later) potentially per-week ratios.
// Spec §6.1. Custom rather than MUI LinearProgress because we want exact
// height + token-driven colors + minimal DOM.
export default function ProgressBar({
  value,
  max = 100,
  ariaLabel,
}: ProgressBarProps) {
  const theme = useTheme();
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;

  return (
    <Box
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={ariaLabel}
      sx={{
        width: "100%",
        height: 4,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.text.secondary, 0.12),
        overflow: "hidden",
        "@media (prefers-reduced-motion: reduce)": {
          "& > *": { transition: "none" },
        },
      }}
    >
      <Box
        sx={{
          width: `${pct}%`,
          height: "100%",
          bgcolor: "secondary.main",
          borderRadius: 2,
          transition: theme.transitions.create("width", { duration: 180 }),
        }}
      />
    </Box>
  );
}
